import { api } from "@/api";
import { QueryProps } from "@/types/Api";

// Tokens are returned ALREADY MASKED by the backend (bullets + last 5 chars,
// e.g. "•••••••2A3F9"); empty string when unset. Plaintext never leaves the
// server. The UI shows the masked value and only sends a token field when the
// admin types a new one (see UpdateTelegramSettingsParams).
// Which bot a token really is (Telegram getMe). "unset" = no token,
// "invalid" = Telegram rejected the token.
export interface TelegramBotIdentity {
  status: "ok" | "unset" | "invalid";
  username: string | null;
}

export interface TelegramSettings {
  enabled: boolean;
  /** Coupon notifications on/off for the whole system. */
  notificationsEnabled: boolean;
  groupId: string | null;
  chatTokenMasked: string;
  twofaTokenMasked: string;
  chatBot: TelegramBotIdentity;
  twofaBot: TelegramBotIdentity;
}

export type TelegramBotSlot = "chat" | "twofa";

export const getTelegramSettings = async (
  props?: QueryProps<void>
): Promise<TelegramSettings> => {
  const res = await api.client.get("/settings/telegram", {
    signal: props?.signal,
  });
  return res.data;
};

// An omitted/empty token field means "keep the stored token" — the backend only
// replaces a token when a non-empty value is sent. Step-up protected on the
// backend; the axios interceptor transparently prompts and retries.
export interface UpdateTelegramSettingsParams {
  enabled?: boolean;
  notificationsEnabled?: boolean;
  groupId?: string;
  chatBotToken?: string;
  twofaBotToken?: string;
}

export const updateTelegramSettings = async (
  params: UpdateTelegramSettingsParams
): Promise<TelegramSettings> => {
  const res = await api.client.put("/settings/telegram", params);
  return res.data;
};

// Sends a test message with one bot: "chat" → the support group, "twofa" →
// the admin's own Telegram chat. Step-up protected. Resolves on 200; rejects
// with an axios error carrying the backend's clear message.
export const testTelegramSettings = async (
  bot: TelegramBotSlot
): Promise<void> => {
  await api.client.post("/settings/telegram/test", { bot });
};
